import * as React from "react";
import { cn } from "../../lib/utils";
import {
  AlertCircle,
  AlertTriangle,
  Info,
  ShieldAlert,
  ShieldX,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";
import { Badge } from "../badge";
import { Button } from "../button";

export type IssueSeverity = "critical" | "high" | "medium" | "low" | "info";

export interface Issue {
  /** Unique identifier */
  id?: string;
  /** Issue code (e.g., "MISSING_HSTS") */
  code?: string;
  /** Severity level */
  severity: IssueSeverity;
  /** Issue message/title */
  message: string;
  /** Detailed description */
  description?: string;
  /** Remediation guidance */
  remediation?: string;
  /** External reference URL */
  referenceUrl?: string;
  /** Module that generated this issue */
  module?: string;
  /** Asset affected */
  asset?: string;
}

export interface IssuesListProps {
  /** Array of issues to display */
  issues: Issue[];
  /** Group issues by severity */
  groupBySeverity?: boolean;
  /** Show severity section counts */
  showCounts?: boolean;
  /** Maximum issues to show before "show more" */
  maxVisible?: number;
  /** Custom class name */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
}

// Severity configuration
const SEVERITY_CONFIG: Record<
  IssueSeverity,
  {
    icon: typeof AlertCircle;
    label: string;
    order: number;
    colors: {
      text: string;
      bg: string;
      border: string;
      badge: string;
    };
  }
> = {
  critical: {
    icon: ShieldX,
    label: "Critical",
    order: 0,
    colors: {
      text: "text-red-400",
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      badge: "bg-red-500/15 text-red-400 border-red-500/40",
    },
  },
  high: {
    icon: ShieldAlert,
    label: "High",
    order: 1,
    colors: {
      text: "text-orange-400",
      bg: "bg-orange-500/10",
      border: "border-orange-500/30",
      badge: "bg-orange-500/15 text-orange-400 border-orange-500/40",
    },
  },
  medium: {
    icon: AlertTriangle,
    label: "Medium",
    order: 2,
    colors: {
      text: "text-amber-400",
      bg: "bg-amber-500/10",
      border: "border-amber-500/30",
      badge: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    },
  },
  low: {
    icon: AlertCircle,
    label: "Low",
    order: 3,
    colors: {
      text: "text-cyan-400",
      bg: "bg-cyan-500/10",
      border: "border-cyan-500/30",
      badge: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40",
    },
  },
  info: {
    icon: Info,
    label: "Info",
    order: 4,
    colors: {
      text: "text-slate-400",
      bg: "bg-slate-500/10",
      border: "border-slate-500/30",
      badge: "bg-slate-500/15 text-slate-400 border-slate-500/40",
    },
  },
};

// Severity badge component
export function SeverityBadge({
  severity,
  className,
}: {
  severity: IssueSeverity | string;
  className?: string;
}) {
  const validSeverity = getValidSeverity(severity);
  const config = SEVERITY_CONFIG[validSeverity];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium",
        config.colors.badge,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

// Helper to get valid severity
function getValidSeverity(severity: unknown): IssueSeverity {
  const validSeverities: IssueSeverity[] = ["critical", "high", "medium", "low", "info"];
  if (typeof severity === "string" && validSeverities.includes(severity as IssueSeverity)) {
    return severity as IssueSeverity;
  }
  return "info";
}

// Individual issue item
function IssueItem({ issue }: { issue: Issue }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const validSeverity = getValidSeverity(issue.severity);
  const config = SEVERITY_CONFIG[validSeverity];
  const Icon = config.icon;

  const hasDetails = issue.description || issue.remediation || issue.referenceUrl;

  const handleCopy = () => {
    const text = [
      issue.code ? `[${issue.code}]` : "",
      issue.message,
      issue.description ? `\nDescription: ${issue.description}` : "",
      issue.remediation ? `\nRemediation: ${issue.remediation}` : "",
    ]
      .filter(Boolean)
      .join(" ");

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden transition-colors",
        config.colors.border,
        hasDetails && "hover:border-opacity-60"
      )}
    >
      <div
        className={cn(
          "flex items-start gap-3 p-3",
          hasDetails && "cursor-pointer",
          config.colors.bg
        )}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <Icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", config.colors.text)} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className={cn("flex-1 min-w-0", issue.asset && "grid grid-cols-[1fr_200px] items-start gap-4")}>
              <div className="min-w-0">
                {issue.code && (
                  <span className="text-xs font-mono text-muted-foreground mr-2">
                    [{issue.code}]
                  </span>
                )}
                <span className="text-sm">{issue.message}</span>
              </div>
              {issue.asset && (
                <span className="text-xs font-mono text-muted-foreground truncate mt-0.5" title={issue.asset}>
                  {issue.asset}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                {copied ? (
                  <Check className="h-3 w-3 text-emerald-400" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
              {hasDetails && (
                isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )
              )}
            </div>
          </div>

          {issue.module && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                Module: <span className="font-mono">{issue.module}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {isExpanded && hasDetails && (
        <div className="border-t border-border p-4 space-y-3 bg-background/50">
          {issue.description && (
            <div>
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Description
              </h5>
              <p className="text-sm text-foreground">{issue.description}</p>
            </div>
          )}

          {issue.remediation && (
            <div>
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Remediation
              </h5>
              <p className="text-sm text-foreground">{issue.remediation}</p>
            </div>
          )}

          {issue.referenceUrl && (
            <a
              href={issue.referenceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              Learn more
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Severity section with collapsible list
function SeveritySection({
  severity,
  issues,
  defaultExpanded = true,
}: {
  severity: IssueSeverity;
  issues: Issue[];
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const config = SEVERITY_CONFIG[severity];
  const Icon = config.icon;

  if (issues.length === 0) return null;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 py-2 text-left hover:opacity-80 transition-opacity"
      >
        <Icon className={cn("h-4 w-4", config.colors.text)} />
        <span className={cn("font-medium text-sm", config.colors.text)}>
          {config.label}
        </span>
        <Badge
          variant="outline"
          className={cn("ml-1 px-1.5 py-0 text-xs", config.colors.badge)}
        >
          {issues.length}
        </Badge>
        <div className="flex-1" />
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="space-y-2 pl-6">
          {issues.map((issue, index) => (
            <IssueItem key={issue.id || `${severity}-${index}`} issue={issue} />
          ))}
        </div>
      )}
    </div>
  );
}

export function IssuesList({
  issues,
  groupBySeverity = true,
  showCounts = true,
  maxVisible,
  className,
  emptyMessage = "No issues found",
}: IssuesListProps) {
  const [showAll, setShowAll] = React.useState(false);

  if (issues.length === 0) {
    return (
      <div className={cn("text-center py-8", className)}>
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 mb-3">
          <Check className="h-6 w-6 text-emerald-400" />
        </div>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  // Group issues by severity
  const groupedIssues = React.useMemo(() => {
    const groups: Record<IssueSeverity, Issue[]> = {
      critical: [],
      high: [],
      medium: [],
      low: [],
      info: [],
    };

    issues.forEach((issue) => {
      const severity = getValidSeverity(issue.severity);
      groups[severity].push(issue);
    });

    return groups;
  }, [issues]);

  // Summary counts
  const counts = React.useMemo(() => {
    return Object.entries(groupedIssues)
      .filter(([, items]) => items.length > 0)
      .map(([severity, items]) => ({
        severity: severity as IssueSeverity,
        count: items.length,
      }))
      .sort((a, b) => SEVERITY_CONFIG[a.severity].order - SEVERITY_CONFIG[b.severity].order);
  }, [groupedIssues]);

  // Apply max visible limit
  const visibleIssues = React.useMemo(() => {
    if (!maxVisible || showAll) return issues;
    return issues.slice(0, maxVisible);
  }, [issues, maxVisible, showAll]);

  const hiddenCount = maxVisible && !showAll ? issues.length - maxVisible : 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary bar */}
      {showCounts && counts.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Issues:</span>
          {counts.map(({ severity, count }) => (
            <Badge
              key={severity}
              variant="outline"
              className={cn(
                "px-2 py-0.5 text-xs gap-1",
                SEVERITY_CONFIG[severity].colors.badge
              )}
            >
              {SEVERITY_CONFIG[severity].label}: {count}
            </Badge>
          ))}
        </div>
      )}

      {/* Issues list */}
      {groupBySeverity ? (
        <div className="space-y-4">
          {(["critical", "high", "medium", "low", "info"] as IssueSeverity[]).map(
            (severity) =>
              groupedIssues[severity].length > 0 && (
                <SeveritySection
                  key={severity}
                  severity={severity}
                  issues={groupedIssues[severity]}
                  defaultExpanded={severity === "critical" || severity === "high"}
                />
              )
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleIssues.map((issue, index) => (
            <IssueItem key={issue.id || index} issue={issue} />
          ))}
        </div>
      )}

      {/* Show more button */}
      {hiddenCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(true)}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          Show {hiddenCount} more issues
          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

IssuesList.displayName = "IssuesList";
SeverityBadge.displayName = "SeverityBadge";
